import {
  ACTIONMODEID_DATA_INDEX,
  ACTIVEUNIT_DATA_INDEX,
  AGSECTORID_DATA_INDEX,
  ATTRUNIT_DATA_INDEX,
  CHEMICALGROUPID_DATA_INDEX,
  CO2E_UNIT_DATA_INDEX,
  CONCENTRATION_UNIT_DATA_INDEX,
  CROP_DATA_INDEX,
  GENUSSPECIESID_DATA_INDEX,
  MAINENDUSEID_DATA_INDEX,
  MANUFACTURERID_DATA_INDEX,
  MOAGROUPID_DATA_INDEX,
  OPERATION_UNIT_DATA_INDEX,
  PACKSIZEUNIT_DATA_INDEX,
  PARAMETERID,
  PHYSICALSTATEID_DATA_INDEX,
  PRODUCTGRADEID_DATA_INDEX,
  PRODUCTGROUPS_DATA_INDEX,
  PRODUCTUNIT_DATA_INDEX,
  RAINFAST_UNIT_DATA_INDEX,
  RATE_UNIT_DATA_INDEX,
  REENTRY_UNIT_DATA_INDEX,
  SEGMENT_DATA_INDEX,
  SPECIESTYPEID_DATA_INDEX,
  SUPPLIERID_DATA_INDEX,
  TECHSTACKID_DATA_INDEX,
} from '../constants/Constant';

import { IUnit } from './IInput';
import { IActiveConstituent, IGcProductGroup } from './IInputStep';

export type CollectionType = 'Reference' | 'Regular';

export enum UnitCategory {
  Unspecified = 0,
  General = 1,
  PlantProtection = 2,
  SoilInput = 4,
  Cost = 8,
  Time = 16,
  Production = 32,
}

export enum UnitParameterType {
  Area = 'Area',
  CostPerAra = 'Cost per area',
  CarbonEmissions = 'Carbon Emissions',
  Length = 'Length',
  PlantProtectionSmallLiquid = 'Plant Protection - Small Liquid',
  PlantProtectionSmallSolid = 'Plant Protection - Small Solid',
  PlantProtectionLargeLiquid = 'Plant Protection - Large Liquid',
  PlantProtectionLargeSolid = 'Plant Protection - Large Solid',
  PlantbackTime = 'Plantback Time',
  PlantProductionLargeSolid = 'Plant Production - Large Solid',
  Pressure = 'Pressure',
  RainfastTime = 'Rainfast Time',
  Ratio = 'Ratio',
  ReentryTime = 'Reentry Time',
  SoilInputSmallLiquid = 'Soil Input - Small Liquid',
  SoilInputSmallSolid = 'Soil Input - Small Solid',
  SoilInputLargeLiquid = 'Soil Input - Large Liquid',
  SoilInputLargeSolid = 'Soil Input - Large Solid',
  Speed = 'Speed',
  Temperature = 'Temperature',
  Weight = 'Weight',
  WeightPerArea = 'Weight per area',
}

export interface IMenuItemsType {
  key: string;
  label: string;
  disabled?: boolean;
  children?: IMenuItemsType[];
  type?: CollectionType;
  isDivider?: boolean;
}

export interface ExpandedDataType {
  key: React.Key;
  date: string;
  name: string;
  upgradeNum: string;
}

export interface OperationSubMenuType {
  key: string;
  label: string;
}

export interface GUnit {
  name: string;
  code: string;
  parameterId: string;
  snapParameter: string;
  isBaseUnit: boolean;
  multiplier: number;
  // Bitmask — flags can be combined (e.g. 6 = PlantProtection|SoilInput), per
  // the GUnit collection's documented schema. Unlike snapParameter (a raw
  // backend string we've already found inconsistent formatting in), this
  // field is officially typed `enum UnitCategory` server-side.
  category: UnitCategory;
  globalId: string;
  partner: number;
  partnerId: string;
  segments: GBase[];
  orgId: string;
  id: string;
  version: number;
  dateCreated: string;
  creatorId: string;
  snapCreator: string;
  dateModified: string;
  modifierId: string;
  snapModifier: string;
  sourceId: string;
  isActive: boolean;
}

export interface GUnitOption {
  name: string;
  label: string;
  id: string;
  globalId: string;
}

export interface GcVariety {
  varietyId: string;
  varietyName: string;
  techStackId?: string | null;
  snapTechStack?: string | null;
  manufacturerId?: string | null;
  snapManufacturer?: string | null;
  supplierId?: string | null;
  snapSupplier?: string | null;
}

/** GSeedSpecies harvest/product/residue values copied onto new production steps. */
export type SeedSpeciesProductionKey = Extract<
  keyof GSeedSpecies,
  `harvest${string}` | `product${string}` | `residue${string}`
>;

// Record over the key union, so tsc fails if GSeedSpecies gains a harvest*/product*/residue* key missing here.
const SEED_SPECIES_PRODUCTION_KEY_SET: Record<
  SeedSpeciesProductionKey,
  true
> = {
  harvestPriceLow: true,
  harvestPrice: true,
  harvestPriceHigh: true,
  harvestProdLow: true,
  harvestProd: true,
  harvestProdHigh: true,
  productN: true,
  productP: true,
  productK: true,
  productS: true,
  productCa: true,
  productMg: true,
  productNa: true,
  productB: true,
  productCu: true,
  productZn: true,
  productMn: true,
  productFe: true,
  productMo: true,
  productCl: true,
  productSi: true,
  residueN: true,
  residueP: true,
  residueK: true,
  residueS: true,
  residueCa: true,
  residueMg: true,
  residueNa: true,
  residueB: true,
  residueCu: true,
  residueZn: true,
  residueMn: true,
  residueFe: true,
  residueMo: true,
  residueCl: true,
  residueSi: true,
};

export const SEED_SPECIES_PRODUCTION_KEYS = Object.keys(
  SEED_SPECIES_PRODUCTION_KEY_SET
) as SeedSpeciesProductionKey[];

export type SeedSpeciesProductionFields = {
  [K in SeedSpeciesProductionKey]: NonNullable<GSeedSpecies[K]> | null;
};

export interface MergedCrop
  extends Partial<Pick<GSeedSpecies, SeedSpeciesProductionKey>> {
  globalId: string;
  name: string;
  varieties: GcVariety[];
  cost?: number | null;
  rate?: number | null;
  rateUnit?: IUnit | null;
}

export interface GcSpecWithholdPeriod {
  periodType?: number | null;
  cropId?: string | null;
  snapCropName?: string | null;
  period?: number | null;
}

export interface GProductLabel {
  labelId: string;
  labelName?: string | null;
  labelUrl?: string | null;
  labelNumber?: string | null;
  labelDate?: string | null;
}

export interface GSupplierNames {
  id: string;
  name: string;
}

export interface IGOrganisationUserAccess {
  userId: string;
  access: number;
}

export interface IGOrganisationTeamAccess {
  teamId: string;
  userAccesses: IGOrganisationUserAccess[];
}

export interface IGOrganisation {
  id: string;
  version: number;
  dateCreated: string;
  creatorId: string | null;
  snapCreator: string | null;
  dateModified: string;
  modifierId: string | null;
  snapModifier: string | null;
  sourceId: string;
  isActive: boolean;
  orgId: string;
  orgName: string;
  userAccesses: IGOrganisationUserAccess[];
  teamAccesses: IGOrganisationTeamAccess[];
  lastChangeId: string | null;
}

export interface InitialGlobalCatalogueProps {
  collectionSelected: string;
  activeGOrganisation: string;
  gcTableData: object[];
  loading: boolean;
  collectionOptions: {
    [ACTIONMODEID_DATA_INDEX]: GBase[];
    [ACTIVEUNIT_DATA_INDEX]: GUnitOption[];
    [AGSECTORID_DATA_INDEX]: GBase[];
    [ATTRUNIT_DATA_INDEX]: GUnitOption[];
    [CHEMICALGROUPID_DATA_INDEX]: GBase[];
    [CO2E_UNIT_DATA_INDEX]: GUnitOption[];
    [CONCENTRATION_UNIT_DATA_INDEX]: GUnitOption[];
    [CROP_DATA_INDEX]: GSeedSpecies[];
    [GENUSSPECIESID_DATA_INDEX]: GBase[];
    [MAINENDUSEID_DATA_INDEX]: GBase[];
    [MANUFACTURERID_DATA_INDEX]: GBase[];
    [MOAGROUPID_DATA_INDEX]: GBase[];
    [OPERATION_UNIT_DATA_INDEX]: GUnitOption[];
    [PACKSIZEUNIT_DATA_INDEX]: GUnitOption[];
    [PARAMETERID]: GBase[];
    [PRODUCTGRADEID_DATA_INDEX]: GBase[];
    [PRODUCTGROUPS_DATA_INDEX]: GBase[];
    [PRODUCTUNIT_DATA_INDEX]: GUnitOption[];
    [PHYSICALSTATEID_DATA_INDEX]: GBase[];
    [RAINFAST_UNIT_DATA_INDEX]: GUnitOption[];
    [RATE_UNIT_DATA_INDEX]: GUnitOption[];
    [REENTRY_UNIT_DATA_INDEX]: GUnitOption[];
    [SEGMENT_DATA_INDEX]: GBase[];
    [SPECIESTYPEID_DATA_INDEX]: GBase[];
    [SUPPLIERID_DATA_INDEX]: GSupplierNames[];
    [TECHSTACKID_DATA_INDEX]: GBase[];
  };
  filters: {
    pagination: {
      pageSize: number;
      current: number;
      total?: number;
      hasMore?: boolean;
      pageIndex?: number;
    };
    segment: {
      segmentId: string;
    };
    sorter: {
      [collection: string]: {
        dataIndex: string;
        order: 'ascend' | 'descend' | null;
      };
    };
  };
  searchState: {
    searchText: string;
    filterManufacturers: string[];
    isSearchApplied: boolean;
  };
}

export interface IConfigTypes {
  dataIndex: string; // Field identifier in the data source.
  defaultOptions: string; // Predefined options for dropdowns or selects.
  key: string; // Unique key for the column.
  title: string; // Display label for the column or field.
  type: string; // Data type of the column or field (e.g., text, number, switch, date, select, multiselect).
  validation: string; // Validation rules for the field.

  editable?: string; // Indicates if the column is editable in the table.
  sorter?: string; // Sorting type for the column (e.g., string, number).
  width?: string; // Width of the column in the table.

  disabled?: string; // Disables the field for user interaction.
  hiddenField?: string; // Specify attribute level hidden fields.
  isGlobalHidden?: string; // Hides the field for global level.
  isHidden?: string; // Hides the field.
  showOnCreate?: boolean; // Shows the field in create mode.

  digitsAfterDecimal?: number; // Number of digits after the decimal point. (required for double type)
  trimTrailingZeroes?: boolean; // Trims trailing zeroes from the decimal value.
  customizedLabel?: string; // Customized label for the select options.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listItems?: any; // config for nested fields. this is for the attributes with type list in right side panel.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: any; // config for nested fields. this is for the subrow in the table.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupItems?: any; // config for nested fields. this is for the attributes with type group in right side panel.
}

export interface GBase {
  globalId: string;
  orgId: string;
  name: string;
  id: string;
  version: number;
  dateCreated: string;
  creatorId: string;
  snapCreator: string;
  dateModified: string;
  modifierId: string;
  snapModifier: string;
  sourceId: string;
  isActive: boolean;
}

export interface GBiostimulant extends GBase {
  price?: number | null;
  rate?: number | null;
  rateUnit?: IUnit | null;
  actionModeId?: string | null;
  snapActionMode?: string | null;
  productGroups: IGcProductGroup[];
  manufacturerId?: string | null;
  snapManufacturer?: string | null;
  supplierId?: string | null;
  snapSupplier?: string | null;
}

export interface GConstraint extends GBase {
  speciesTypeId?: string | null;
  snapSpeciesType?: string | null;
  segments?: GBase[];
}

export interface GOperation extends GBase {
  cost?: number | null;
  operationUnit?: IUnit | null;
  co2eScope1?: number | null;
  co2eScope2?: number | null;
  co2eScope3?: number | null;
  co2eUnit?: IUnit | null;
}

export interface GPlantProtection extends GBase {
  registeredName?: string | null;
  useWater: boolean;
  productGroups: IGcProductGroup[];
  chemicalGroupId?: string | null;
  snapChemicalGroup?: string | null;
  activeUnit?: IUnit | null;
  activeConstituents: IActiveConstituent[];
  formulationCode?: string | null;
  formulation?: string | null;
  rate?: number | null;
  rateUnit?: IUnit | null;
  price?: number | null;
  status: number;
  apvmaNumber?: string | null;
  poisonSchedule: number;
  dropletSize: number;
  withholdPeriodGrazing?: number | null;
  withholdPeriodCutting?: number | null;
  withholdPeriodHarvest?: number | null;
  withholdPeriodPlantback?: number | null;
  reentry?: number | null;
  reentryUnit?: IUnit | null;
  reentryNotes?: string | null;
  rainfast?: number | null;
  rainfastUnit?: IUnit | null;
  manufacturerId?: string | null;
  snapManufacturer?: string | null;
  supplierId?: string | null;
  snapSupplier?: string | null;
  packSize?: number | null;
  packSizeUnit?: IUnit | null;
  labels: GProductLabel[];
  specWithholdPeriods: GcSpecWithholdPeriod[];
}

export interface GSeedSpecies extends GBase {
  varieties: GcVariety[];
  genusSpeciesId?: string | null;
  snapGenusSpecies?: string | null;
  cost?: number | null;
  rate?: number | null;
  rateUnit?: IUnit | null;
  harvestPriceLow?: number | null;
  harvestPrice?: number | null;
  harvestPriceHigh?: number | null;
  harvestProdLow?: number | null;
  harvestProd?: number | null;
  harvestProdHigh?: number | null;
  mainEndUseId?: string | null;
  snapMainEndUse?: string | null;
  agSectorId?: string | null;
  snapAgSector?: string | null;
  origin?: string | null;
  productN?: number | null;
  productP?: number | null;
  productK?: number | null;
  productS?: number | null;
  productCa?: number | null;
  productMg?: number | null;
  productNa?: number | null;
  productB?: number | null;
  productCu?: number | null;
  productZn?: number | null;
  productMn?: number | null;
  productFe?: number | null;
  productMo?: number | null;
  productCl?: number | null;
  productSi?: number | null;
  residueN?: number | null;
  residueP?: number | null;
  residueK?: number | null;
  residueS?: number | null;
  residueCa?: number | null;
  residueMg?: number | null;
  residueNa?: number | null;
  residueB?: number | null;
  residueCu?: number | null;
  residueZn?: number | null;
  residueMn?: number | null;
  residueFe?: number | null;
  residueMo?: number | null;
  residueCl?: number | null;
  residueSi?: number | null;
}

export interface GSoilInput extends GBase {
  price?: number | null;
  rate?: number | null;
  rateUnit?: IUnit | null;
  physicalStateId?: string | null;
  snapPhysicalState?: string | null;
  techStackId?: string | null;
  snapTechStack?: string | null;
  productGroups: IGcProductGroup[];
  efficiency?: number | null;
  nutrientN?: number | null;
  nutrientP?: number | null;
  nutrientK?: number | null;
  nutrientS?: number | null;
  nutrientCa?: number | null;
  nutrientMg?: number | null;
  nutrientNa?: number | null;
  nutrientB?: number | null;
  nutrientCu?: number | null;
  nutrientFe?: number | null;
  nutrientMn?: number | null;
  nutrientZn?: number | null;
  nutrientMo?: number | null;
  nutrientCl?: number | null;
  nutrientSi?: number | null;
  nutrientNi?: number | null;
  nutrientCo?: number | null;
  additive?: string | null;
  isSprays: boolean;
  isSeedDressing: boolean;
  co2eScope1?: number | null;
  co2eScope2?: number | null;
  co2eScope3?: number | null;
  co2eUnit?: IUnit | null;
  manufacturerId?: string | null;
  snapManufacturer?: string | null;
  supplierId?: string | null;
  snapSupplier?: string | null;
}

export interface GTiming extends GBase {
  month: number | null;
  day: number | null;
}
